import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * STANDARD TAB LAYOUT
 * 
 * Every tab in the app should use this layout for consistency.
 * 
 * Features:
 * - Consistent title (text-2xl font-bold on mobile, text-3xl on desktop)
 * - Consistent subtitle styling
 * - Actions area (top right on desktop, full width on mobile)
 * - Loading state (simple centered spinner)
 * - Mobile responsive
 * 
 * Usage:
 * <TabPageLayout
 *   title="Revenue"
 *   subtitle="Track your earnings"
 *   loading={isLoading}
 *   actions={<Button>Export</Button>}
 * >
 *   {content}
 * </TabPageLayout>
 */
const TabPageLayout = ({ 
  title, 
  subtitle, 
  actions,
  loading = false,
  children 
}) => {
  return (
    <div className="space-y-6 mt-2">
      {/* Page Header - Always visible even when loading */}
      {(title || subtitle || actions) && (
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          {/* Title & Subtitle */}
          <div className="flex-1 min-w-0">
            {title && (
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 truncate">
                {title}
              </h1>
            )}
            {subtitle && (
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                {subtitle}
              </p>
            )}
          </div>
          
          {/* Actions */}
          {actions && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-shrink-0">
              {actions}
            </div>
          )}
        </div>
      )}
      
      {/* Page Content */}
      {loading ? (
        <PageLoader />
      ) : (
        <div>{children}</div>
      )}
    </div>
  );
};

/**
 * STANDARD PAGE LOADER
 * 
 * Simple centered spinner. Used by TabPageLayout when loading={true}.
 * Can also be used standalone.
 * 
 * Usage:
 * <PageLoader />
 * <PageLoader minHeight="h-32" />
 */
export const PageLoader = ({ minHeight = 'h-64' }) => {
  return (
    <div className={`flex items-center justify-center ${minHeight}`}>
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
    </div>
  );
};

/**
 * STANDARD DATA CARD
 * 
 * Flat design card with border for containing content.
 * 
 * Usage:
 * <DataCard>
 *   {content}
 * </DataCard>
 * 
 * <DataCard title="Revenue" subtitle="This month">
 *   {content}
 * </DataCard>
 */
export const DataCard = ({ 
  title, 
  subtitle, 
  actions,
  className = '',
  children 
}) => {
  return (
    <div className={`bg-white rounded-xl overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.04)] ${className}`}>
      {(title || subtitle || actions) && (
        <div className="px-4 py-3 sm:px-6 sm:py-4 border-b border-gray-100 flex items-center justify-between gap-4">
          <div className="min-w-0">
            {title && (
              <h3 className="font-semibold text-gray-900 truncate">{title}</h3>
            )}
            {subtitle && (
              <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
            )}
          </div>
          {actions && (
            <div className="flex-shrink-0">{actions}</div>
          )}
        </div>
      )}
      <div className="p-4 sm:p-6">
        {children}
      </div>
    </div>
  );
};

/**
 * STANDARD STAT CARD
 * 
 * For displaying key metrics with optional trend indicator.
 * 
 * Usage:
 * <StatCard
 *   label="Revenue"
 *   value="$12,345"
 *   trend={+12.5}
 *   icon={<DollarSign />}
 *   color="green"
 * />
 */
export const StatCard = ({
  label,
  value,
  subValue,
  trend,
  icon,
  color = 'gray', // gray, green, blue, purple, orange, cyan
  onClick
}) => {
  const colorMap = {
    gray: 'from-gray-50 to-slate-50 border-gray-200',
    green: 'from-green-50 to-emerald-50 border-green-100',
    blue: 'from-blue-50 to-indigo-50 border-blue-100',
    purple: 'from-purple-50 to-violet-50 border-purple-100',
    orange: 'from-orange-50 to-amber-50 border-orange-200',
    cyan: 'from-cyan-50 to-teal-50 border-cyan-100'
  };
  
  const iconColorMap = {
    gray: 'bg-gray-100 text-gray-600',
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
    cyan: 'bg-cyan-100 text-cyan-600'
  };
  
  const textColorMap = {
    gray: 'text-gray-800',
    green: 'text-green-800',
    blue: 'text-blue-800',
    purple: 'text-purple-800',
    orange: 'text-orange-800',
    cyan: 'text-cyan-800'
  };
  
  const labelColorMap = {
    gray: 'text-gray-700',
    green: 'text-green-700',
    blue: 'text-blue-700',
    purple: 'text-purple-700',
    orange: 'text-orange-700',
    cyan: 'text-cyan-700'
  };

  return (
    <div 
      className={`bg-gradient-to-br ${colorMap[color]} border rounded-xl p-4 sm:p-5 ${onClick ? 'cursor-pointer hover:shadow-sm transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-3">
        {icon && (
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconColorMap[color]}`}>
            {React.cloneElement(icon, { className: 'w-5 h-5' })}
          </div>
        )}
        {trend !== undefined && (
          <TrendIndicator value={trend} />
        )}
      </div>
      <p className={`text-xs uppercase tracking-wide font-medium ${labelColorMap[color]}`}>
        {label}
      </p>
      <p className={`text-2xl sm:text-3xl font-bold ${textColorMap[color]}`}>
        {value}
      </p>
      {subValue && (
        <p className="text-xs text-gray-500 mt-1">{subValue}</p>
      )}
    </div>
  );
};

/**
 * TREND INDICATOR
 * Shows up/down trend with percentage
 */
const TrendIndicator = ({ value }) => {
  if (value === 0 || value === undefined || value === null) return null;
  
  const isPositive = value > 0;
  return (
    <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${
      isPositive 
        ? 'bg-green-100 text-green-700' 
        : 'bg-red-100 text-red-700'
    }`}>
      {isPositive ? '↑' : '↓'} {Math.abs(value)}%
    </span>
  );
};

/**
 * EMPTY STATE
 * 
 * Consistent empty state display.
 * 
 * Usage:
 * <EmptyState
 *   icon={<FileText />}
 *   title="No documents"
 *   description="Upload your first document to get started"
 *   action={<Button>Upload</Button>}
 * />
 */
export const EmptyState = ({
  icon,
  title,
  description,
  action
}) => {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      {icon && (
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          {React.cloneElement(icon, { className: 'w-8 h-8 text-gray-400' })}
        </div>
      )}
      {title && (
        <h3 className="text-lg font-medium text-gray-900 mb-1">{title}</h3>
      )}
      {description && (
        <p className="text-sm text-gray-500 max-w-sm mb-4">{description}</p>
      )}
      {action}
    </div>
  );
};

export default TabPageLayout;
